import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BatchMoveContainersInput, BatchMoveContainersOutput, MoveContainerMode } from 'src/models/batchMoveContainers';
import { resetIsLoading, resetUiAlertState, setIsLoading, setUiAlertState } from 'src/store/common-slices/uiSlice';
import * as ErrorConstants from 'src/localization/string-ids/error-constants';
import * as SuccessConstants from 'src/localization/string-ids/success-constants';
import { AlertLevels } from 'src/models/alert-model';
import { resetContainerState } from 'src/store/common-slices/getContainerSlice';
import RIVBFFService from 'src/services/riv-bff-service';
import { ContainerDetailsModel } from 'src/components/common/container-details/ContainerDetailsModel';
import { RIVApiMethods } from 'src/constants/api-method-constants';

export enum BatchMoveContainersPages {
    SCAN_CHILD_CONTAINERS = 'ScanChildContainers',
    SCAN_DESTINATION_CONTAINER = 'ScanDestinationContainer',
    CLOSE_CONTAINER = 'CloseContainer',
    PRINT_LABEL = 'PrintLabel'
}

export enum BatchMoveContainersMode {
    SINGLE_MODE = 'SingleMode',
    MULTI_MODE = 'MultiMode'
}

export interface batchMoveContainersState {
    childContainers: ContainerDetailsModel[],
    destinationContainerId: string,
    batchMoveMode: BatchMoveContainersMode,
    pageToRender: BatchMoveContainersPages
}

const initialState: batchMoveContainersState = {
    // TODO: Rename it to movingContainers everywhere it's used
    childContainers: [],
    destinationContainerId: '',
    batchMoveMode: BatchMoveContainersMode.SINGLE_MODE,
    pageToRender: BatchMoveContainersPages.SCAN_CHILD_CONTAINERS
}

export const batchMoveContainers = createAsyncThunk<
    BatchMoveContainersOutput,
    BatchMoveContainersInput>(
    'moveContainers/batchMoveContainers',
    async (batchMoveContainersInput: BatchMoveContainersInput, { dispatch, rejectWithValue }) => {
        dispatch(resetUiAlertState());
        dispatch(setIsLoading());
        try {
            const batchMoveContainersOutput: BatchMoveContainersOutput = await RIVBFFService.triggerBatchMoveContainersAPI(batchMoveContainersInput);
            dispatch(resetIsLoading());
            dispatch(resetContainerState());

            // Handle partial success — some containers failed in inventory system
            if (batchMoveContainersOutput.failedContainers && batchMoveContainersOutput.failedContainers.length > 0) {
                const failedCount = batchMoveContainersOutput.failedContainers.length;
                const successCount = batchMoveContainersInput.movingContainers.length - failedCount;
                const failedIds = batchMoveContainersOutput.failedContainers
                    .map(c => c.containerId)
                    .join(', ');
                const successMessage = batchMoveContainersInput.mode === MoveContainerMode.CONTAINERIZE ?
                    SuccessConstants.CONTAINERIZATION_SUCCESS : SuccessConstants.MOVE_SUCCESS;
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.WARNING,
                    alertTitleString: `${successCount} of ${batchMoveContainersInput.movingContainers.length} ${SuccessConstants.CONTAINERS}`,
                    alertMessageString: `${successMessage} ${failedCount} container(s) ${ErrorConstants.INVENTORY_MOVE_PARTIAL_FAILURE}: ${failedIds}`,
                }));
            } else {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.SUCCESS,
                    alertTitleString: `${batchMoveContainersInput.movingContainers.length} ${SuccessConstants.CONTAINERS}`,
                    alertMessageString: batchMoveContainersInput.mode === MoveContainerMode.CONTAINERIZE ?
                        SuccessConstants.CONTAINERIZATION_SUCCESS : SuccessConstants.MOVE_SUCCESS,
                }));
            }
            return batchMoveContainersOutput;
        } catch (error) {
            dispatch(resetIsLoading());
            
            if (error && typeof error === 'object' && 'errorMessage' in error && 'errorStatus' in error) {
                const apiError = error as { errorMessage: string; errorStatus: number };
                
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${batchMoveContainersInput.movingContainers.length} ${SuccessConstants.CONTAINERS}`,
                    alertMessageString: apiError.errorMessage,
                    alertStatus: apiError.errorStatus,
                    UIAction: RIVApiMethods.BATCH_MOVE_CONTAINERS,
                    warehouse: batchMoveContainersInput.warehouseId,
                    associate: batchMoveContainersInput.associate,
                    mode: batchMoveContainersInput.mode
                }));
                return rejectWithValue({
                    data: apiError.errorMessage,
                    status: apiError.errorStatus
                });
            }
            else if (error instanceof Error) {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${batchMoveContainersInput.movingContainers.length} ${SuccessConstants.CONTAINERS}`,
                    alertMessageString: error.message
                }));
                return rejectWithValue(error.message);
            }
            else {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${batchMoveContainersInput.movingContainers.length} ${SuccessConstants.CONTAINERS}`,
                    alertMessageString: ErrorConstants.UNKNOWN_ERROR
                }));
                return rejectWithValue(ErrorConstants.UNKNOWN_ERROR);
            }
        }
    }
);

const batchMoveContainersSlice = createSlice({
    name: 'batchMoveContainers',
    initialState,
    reducers: {
        setPageToRender: (state, action) => {
            state.pageToRender = action.payload;
        },
        resetBatchMovePageToRender: (state) => {
            state.pageToRender = initialState.pageToRender;
        },
        setBatchMoveMode: (state, action) => {
            state.batchMoveMode = action.payload;
        },
        updateChildContainers: (state, action: PayloadAction<ContainerDetailsModel>) => {
            state.childContainers.push(action.payload);
        },
        updateDestinationContainerId: (state, action) => {
            state.destinationContainerId = action.payload;
        },
        resetChildAndDestinationContainers: (state) => {
            state.childContainers = initialState.childContainers;
            state.destinationContainerId = initialState.destinationContainerId;
        },
        resetBatchMoveContainersState: (state) => {
            state.childContainers = initialState.childContainers;
            state.destinationContainerId = initialState.destinationContainerId;
            state.batchMoveMode = initialState.batchMoveMode;
            state.pageToRender = initialState.pageToRender;
        }
    }
});

export const {
    setPageToRender,
    resetBatchMovePageToRender,
    setBatchMoveMode,
    resetChildAndDestinationContainers,
    resetBatchMoveContainersState,
    updateChildContainers,
    updateDestinationContainerId
} = batchMoveContainersSlice.actions;
export default batchMoveContainersSlice.reducer;